import React, { useContext } from 'react';
import styled from 'styled-components';
import { Field, useField, useForm } from 'react-final-form';
import { Toggle, Slider as BaseSlider, FlexMiddle } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { CLOCK_METHODS } from '@ubeya/shared/constants';
import { SettingsSelectField, SettingsTextInput, SettingsCheckBoxField } from 'settings/components/shared';
import Row from 'settings/components/shared/SettingRow';

const Wrapper = styled.div.attrs(() => ({ className: 'timesheet-config-wrapper' }))``;

const Slider = styled(BaseSlider)`
  margin-right: 30px;
  margin-top: 20px;
`;

const ClockMethodSelectField = styled(SettingsSelectField)`
  width: 100%;
  max-width: 360px;

  .select-input {
    max-width: none;
  }
`;

const ClockMethod = ({ slug, name }) => {
  const { t } = useContext(ConfigContext);
  const options = Object.values(CLOCK_METHODS).map(({ slug: clockMethodSlug, value }) => ({
    label: t(clockMethodSlug),
    value
  }));
  return <ClockMethodSelectField title={t(slug)} name={name} options={options} isMulti={false} />;
};

const TimesheetToggleRow = styled(Row).attrs(() => ({ className: 'timesheet-config-toggle' }))``;

const ToggleText = styled.div.attrs(() => ({ className: 'timesheet-config-toggle-text' }))``;

const BoldToggleText = styled(ToggleText)`
  font-weight: 600 !important;
`;

export const TimeAttendanceConfig = ({ enableEventTimeConfig = true, showClockCheckInOutOnly = false }) => {
  const { t, distanceUnitShortTranslationKey, isCrewTimesheetPageEnabled } = useContext(ConfigContext);
  const form = useForm();

  const {
    input: { value: allowDailyEmployeesToEditTimesheetViaApp }
  } = useField('allowDailyEmployeesToEditTimesheetViaApp');

  const {
    input: { value: allowShiftEmployeesToEditTimesheetViaApp }
  } = useField('allowShiftEmployeesToEditTimesheetViaApp');

  const allowEditTimesheetViaApp = allowDailyEmployeesToEditTimesheetViaApp || allowShiftEmployeesToEditTimesheetViaApp;

  const handleAllowEditTimesheetViaAppToggle = (value) => {
    form.change('allowDailyEmployeesToEditTimesheetViaApp', value);
    form.change('allowShiftEmployeesToEditTimesheetViaApp', value);
  };

  const allowClockWithMobileFieldName = showClockCheckInOutOnly ? 'allowClockWithMobile' : 'toAllowClockWithMobile';

  const requireCheckInWithMobileFieldName = showClockCheckInOutOnly
    ? 'requireCheckInWithMobile'
    : 'toRequireCheckInWithMobile';

  const {
    input: { value: allowClockWithMobile }
  } = useField(allowClockWithMobileFieldName);

  const {
    input: { value: toAllowGpsWhenClock }
  } = useField('toAllowGpsWhenClock');

  const {
    input: { value: requireCheckInWithMobile, onChange: onChangeRequireCheckIn }
  } = useField(requireCheckInWithMobileFieldName);

  const {
    input: {
      value: autoClockOutAfterCheckOut,
      onChange: onChangeAutoClockOutAfterCheckOut,
      onBlur: onAutoClockOutAfterCheckOutBlur
    }
  } = useField('autoClockOutAfterCheckOut');

  const {
    // TODO: Not sure why, but you must leave this to make mobileClockMethods works
    // (otherwise it doesnt send the key to server)
    // eslint-disable-next-line unused-imports/no-unused-vars
    input: { value: mobileClockMethods }
  } = useField('mobileClockMethods');

  const handleChangeRequireCheckInOut = (value) => {
    onChangeRequireCheckIn(value);

    if (!value) return handleAutoClockOutAfterCheckOutToggle(value);
  };

  const handleAutoClockOutAfterCheckOutBlur = (e) => {
    if (e.target.value === '' || Number(e.target.value) < 0) {
      onChangeAutoClockOutAfterCheckOut(0);
    }
    onAutoClockOutAfterCheckOutBlur(e);
  };

  const handleAutoClockOutAfterCheckOutToggle = (value) => onChangeAutoClockOutAfterCheckOut(value ? 0 : null);

  return (
    <Wrapper>
      {enableEventTimeConfig && (
        <TimesheetToggleRow data-setting-field={allowClockWithMobileFieldName}>
          <Field name={allowClockWithMobileFieldName} render={({ input }) => <Toggle {...input} />} />
          <ToggleText>{t('allowClockMobile')}</ToggleText>
        </TimesheetToggleRow>
      )}

      {enableEventTimeConfig && allowClockWithMobile && (
        <>
          <Row data-setting-field="mobileClockMethods.clockIn.method">
            <ClockMethod slug="clockInMethod" name="mobileClockMethods.clockIn.method" />
          </Row>

          <Row data-setting-field="mobileClockMethods.clockOut.method">
            <ClockMethod slug="clockOutMethod" name="mobileClockMethods.clockOut.method" />
          </Row>

          <TimesheetToggleRow data-setting-field={requireCheckInWithMobileFieldName}>
            <Field
              name={requireCheckInWithMobileFieldName}
              render={({ input }) => <Toggle {...input} onChange={handleChangeRequireCheckInOut} />}
            />
            <ToggleText>{t('allowCheckInMobile')}</ToggleText>
          </TimesheetToggleRow>

          {requireCheckInWithMobile && (
            <>
              <Row data-setting-field="mobileClockMethods.checkIn.method">
                <ClockMethod slug="checkInMethod" name="mobileClockMethods.checkIn.method" />
              </Row>

              <Row data-setting-field="mobileClockMethods.checkOut.method">
                <ClockMethod slug="checkOutMethod" name="mobileClockMethods.checkOut.method" />
              </Row>
            </>
          )}

          {!showClockCheckInOutOnly && (
            <>
              <TimesheetToggleRow data-setting-field="autoClockOutAfterCheckOut">
                <Toggle
                  onChange={handleAutoClockOutAfterCheckOutToggle}
                  value={autoClockOutAfterCheckOut !== ''}
                  isDisabled={!requireCheckInWithMobile}
                />
                {t('autoClockOutAfterCheckOutToggle')}
              </TimesheetToggleRow>

              {autoClockOutAfterCheckOut !== '' && (
                <Row data-setting-field="autoClockOutAfterCheckOutDescription">
                  {t('autoClockOutAfterCheckOutDescription')}
                  <Field
                    name="autoClockOutAfterCheckOut"
                    render={({ input }) => (
                      <SettingsTextInput type="number" {...input} onBlur={handleAutoClockOutAfterCheckOutBlur} />
                    )}
                  />
                </Row>
              )}

              <TimesheetToggleRow data-setting-field="toAllowGpsWhenClock">
                <Field name="toAllowGpsWhenClock" render={({ input }) => <Toggle {...input} />} />
                <ToggleText>{t('allowGEOClock')}</ToggleText>
              </TimesheetToggleRow>

              {toAllowGpsWhenClock && (
                <>
                  <TimesheetToggleRow data-setting-field="distanceFromShiftAddress">
                    <ToggleText>
                      {t('distanceFromShiftAddress', { unit: t(distanceUnitShortTranslationKey) })}
                    </ToggleText>
                  </TimesheetToggleRow>

                  <FlexMiddle>
                    <Field
                      name="distanceFromShiftAddress"
                      render={({ input }) => <Slider {...input} min={0} max={500} showValue={false} />}
                    />
                    <Field
                      name="distanceFromShiftAddress"
                      render={({ input }) => <SettingsTextInput type="number" {...input} />}
                    />
                  </FlexMiddle>
                </>
              )}

              <TimesheetToggleRow data-setting-field="toSendClockInReminder">
                <Field name="toSendClockInReminder" render={({ input }) => <Toggle {...input} />} />
                <ToggleText>{t('toSendClockInReminder')}</ToggleText>
              </TimesheetToggleRow>

              <TimesheetToggleRow data-setting-field="toSendClockOutReminder">
                <Field name="toSendClockOutReminder" render={({ input }) => <Toggle {...input} />} />
                <ToggleText>{t('toSendClockOutReminder')}</ToggleText>
              </TimesheetToggleRow>

              <TimesheetToggleRow data-setting-field="allowCaptainsReportTheirOwnTimesheet">
                <Field name="allowCaptainsReportTheirOwnTimesheet" render={({ input }) => <Toggle {...input} />} />
                <ToggleText>{t('allowCaptainsReportTheirOwnTimesheet')}</ToggleText>
              </TimesheetToggleRow>

              {isCrewTimesheetPageEnabled && (
                <TimesheetToggleRow data-setting-field="allowEditTimesheetViaApp">
                  <Toggle value={allowEditTimesheetViaApp} onChange={handleAllowEditTimesheetViaAppToggle} />
                  <ToggleText>{t('allowEditTimesheetViaApp')}</ToggleText>
                </TimesheetToggleRow>
              )}

              {isCrewTimesheetPageEnabled && allowEditTimesheetViaApp && (
                <Row>
                  <BoldToggleText>{t('appliesTo')}</BoldToggleText>
                  <SettingsCheckBoxField name="allowDailyEmployeesToEditTimesheetViaApp" title={t('dailyEmployees')} />
                  <SettingsCheckBoxField name="allowShiftEmployeesToEditTimesheetViaApp" title={t('shiftEmployees')} />
                </Row>
              )}
            </>
          )}
        </>
      )}
    </Wrapper>
  );
};
